import React from 'react';
import { useSelector } from 'react-redux';

const OPERATOR = 'Tomáš Zikmunda';
const BUSINESS_ID = '05569621';
const BUSINESS_ADDRESS = 'Ještědská 110/67, 460 08 Liberec';

// Legal contact follows the broker, not the technical platform's sender or test mailbox.
function useContactEmail() {
    return useSelector(state => state.appUI.realEstateAgentTexts.personal?.homePage?.text?.contactEmail)
        || 'zikmunda.reality@gmail.com';
}

function OperatorDetails({ email }) {
    return <>
        {OPERATOR}, IČO {BUSINESS_ID}, se sídlem {BUSINESS_ADDRESS}, e-mail{' '}
        <a href={`mailto:${email}`}>{email}</a>
    </>;
}

// Shared notice for the public page and form modal; operational approval is tracked in the project docs.
export function PrivacyInformation() {
    const email = useContactEmail();

    return <div className="small-text">
        <h4>Informace o zpracování osobních údajů</h4>
        <p>
            Správcem osobních údajů získaných prostřednictvím webu tomas-zikmunda.cz je{' '}
            <OperatorDetails email={email} />. S dotazy ke zpracování údajů nebo žádostí o uplatnění práv
            se obracejte na uvedený e-mail nebo adresu sídla.
        </p>

        <h5>Jaké údaje a proč zpracovávám</h5>
        <p>
            Z kontaktního formuláře zpracovávám jméno, příjmení, e-mail, případně telefon, předmět a text zprávy.
            U poptávky ocenění také údaje o nemovitosti, její adrese a Vámi uvedený odhad ceny. U hypoteční
            kalkulačky zpracovávám Vámi zadané parametry výpočtu a poptávky. Údaje slouží k vyřízení dotazu,
            přípravě individuální nabídky a komunikaci o požadované službě. Nezbytné údaje jsou potřeba,
            abych Vám mohl odpovědět; volitelná pole vyplňovat nemusíte. Bez údajů označených jako povinné
            nelze formulář odeslat; můžete mě ale kontaktovat přímo e-mailem nebo telefonicky.
        </p>
        <p>
            U konkrétní poptávky služby je právním základem provedení opatření před uzavřením smlouvy na Vaši žádost.
            Obecné dotazy vyřizuji na základě oprávněného zájmu na komunikaci s návštěvníky webu. Pokud naváže
            spolupráce, mohou být údaje dále zpracovávány pro plnění smlouvy a zákonných povinností. Na základě
            oprávněného zájmu mohu také chránit své právní nároky a zabezpečovat formuláře proti zneužití.
            Odeslání formuláře ani potvrzení seznámení s tímto poučením nepředstavuje souhlas
            se zasíláním obchodních sdělení. Na Vámi uvedený e-mail může přijít potvrzení přijetí poptávky.
        </p>

        <h5>Komu mohou být údaje zpřístupněny</h5>
        <p>
            K údajům mohou mít v nezbytném rozsahu přístup poskytovatelé technického provozu webu,
            hostingu a e-mailových služeb, aby zajistili doručení zpráv, dostupnost a zabezpečení webu.
            Dodavatel technické správy webu nezpracovává Vaše poptávky pro své vlastní obchodní nabídky
            nebo marketing. Za vyřízení Vaší poptávky odpovídám já jako správce údajů.
        </p>
        <p>
            K ochraně formulářů před zneužitím slouží Google reCAPTCHA. Zpracovává přitom technické
            údaje, například IP adresu a informace o prohlížeči. Podrobnosti uvádějí{' '}
            <a href="https://policies.google.com/privacy?hl=cs" target="_blank" rel="noopener noreferrer">
                zásady ochrany soukromí společnosti Google</a>.
        </p>
        <p>
            Při používání služeb Google, včetně reCAPTCHA a e-mailové schránky, mohou být údaje
            zpracovávány také mimo Evropský hospodářský prostor, zejména v USA. Google popisuje
            příslušné mechanismy ochrany, včetně rámce EU–USA pro ochranu údajů a standardních
            smluvních doložek, na stránce{' '}
            <a href="https://policies.google.com/privacy/frameworks?hl=cs" target="_blank" rel="noopener noreferrer">
                právní rámce pro přenosy dat</a>.
        </p>

        <h5>Jak se určuje doba uchování</h5>
        <ul>
            <li>U dotazů a poptávek podle toho, zda je požadavek ještě vyřizován nebo probíhá přímo
                související komunikace. Ukončení této komunikace je důvodem k posouzení, zda údaje
                potřebují být dále uchovány.</li>
            <li>Pokud naváže smluvní spolupráce, podle doby plnění smlouvy a zákonných povinností,
                které se vztahují na konkrétní dokumenty.</li>
            <li>U údajů potřebných k řešení reklamace, sporu nebo ochraně právních nároků podle
                trvání příslušného řízení a použitelných promlčecích lhůt.</li>
        </ul>
        <p>
            Uchování pro jiný účel není důvodem k ponechání všech údajů z poptávky bez omezení.
            S dotazem na uchování Vašich údajů nebo žádostí o výmaz se můžete obrátit na uvedený kontakt.
        </p>

        <h5>Vaše práva</h5>
        <p>
            Můžete požádat o přístup ke svým údajům, jejich opravu, výmaz nebo omezení zpracování;
            za podmínek právních předpisů také o přenositelnost. Žádost zašlete na kontaktní e-mail
            uvedený výše. Máte také právo
            podat stížnost u{' '}
            <a href="https://uoou.gov.cz/" target="_blank" rel="noopener noreferrer">
                Úřadu pro ochranu osobních údajů</a>.
        </p>
        <p>
            <strong>Právo vznést námitku:</strong> proti zpracování založenému na oprávněném zájmu
            můžete kdykoliv vznést námitku z důvodů týkajících se Vaší konkrétní situace.
        </p>
        <p>
            Údaje z formulářů nepoužívám k automatizovanému rozhodování s právními nebo obdobně
            významnými účinky. Hypoteční kalkulačka poskytuje pouze orientační výpočet.
        </p>
    </div>;
}

// These conditions govern website use and inquiries, not an unconfirmed brokerage or credit contract.
export function WebsiteTerms() {
    const email = useContactEmail();

    return <div className="small-text">
        <h4>Všeobecné obchodní podmínky</h4>
        <p>
            Provozovatelem webu tomas-zikmunda.cz je <OperatorDetails email={email} />.
            Web představuje jeho služby v oblasti nemovitostí a menších developerských projektů.
            Tyto podmínky se týkají používání webu a odesílání poptávek. Konkrétní rozsah služby, cena
            a další závazné podmínky se sjednávají individuálně.
        </p>

        <h5>Poptávky a informace na webu</h5>
        <p>
            Odesláním kontaktního formuláře, poptávky ocenění nebo dotazu z hypoteční kalkulačky
            mi předáváte údaje potřebné k odpovědi. Samotné odeslání nevytváří smlouvu ani závaznou
            objednávku služby. E-mailové potvrzení přijetí pouze potvrzuje doručení poptávky,
            nikoliv uzavření smlouvy. V poptávce uvádějte pravdivé informace a údaje dalších osob pouze tehdy,
            pokud jste k tomu oprávněni.
        </p>
        <p>
            Informace o nemovitostech, developerských projektech a možné spolupráci jsou obecné.
            Výpočty kalkulačky a orientační odhady nepředstavují znalecký posudek, příslib úvěru,
            zaručený výnos ani závaznou nabídku. Podmínky konkrétního projektu nebo financování,
            včetně rizik a odpovědností stran, je nutné dohodnout samostatně.
        </p>

        <h5>Osobní údaje a kontakt</h5>
        <p>
            Zpracování údajů z formulářů popisují samostatné{' '}
            <a href="/osobni-udaje">informace o zpracování osobních údajů</a>.
            S dotazy ke službám nebo k těmto podmínkám se obraťte na{' '}
            <a href={`mailto:${email}`}>{email}</a>.
        </p>
    </div>;
}
